import type { NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { Meta } from '@/layouts';
import { MultibankingPlatformLayout } from '@/layouts/MultibankingPlatformLayout/MultibankingPlatformLayout';
import { MapDepositsFromServer } from '@/services/supabase';
import { Main } from '@/templates/Main';
import type { IBankDepositCard } from '@/types';
import { makeServerProps } from '@/utils/nextjs';

import ApiSingleton from '../../../services/ApiSingleton';

export const getServerSideProps = makeServerProps()
  .withAction(async ({ locale, query }, _, userCountry) => {
    const apiSingleton = ApiSingleton.getInstance();

    try {
      const country = userCountry.name;
      const preparedTerms: any = 'all';
      let isTest;

      if (query?.test_user === 'true') {
        isTest = true;
      }
      if (query?.test_user === 'false') {
        isTest = false;
        return {
          notFound: true,
        };
      }

      // Create promises for concurrent fetching
      const featuredDepositsPromise = apiSingleton.getDepositsListRequest(
        locale || 'en',
        {
          is_test: isTest,
          page: 1,
          countries: [userCountry.alpha2],
          currencies: undefined,
          product_types: ['fixed term deposit'],
          deposit_types: ['ordinary deposit'],
          partner_type: 'platform',
          is_partner: true,
        },
      );

      const bestDepositsPromise = apiSingleton.getDepositsListRequest(
        locale || 'en',
        {
          is_test: isTest,
          page: 1,
          product_types: ['fixed term deposit'],
          deposit_types: ['ordinary deposit'],
          is_partner: true,
          currencies: undefined,
        },
      );

      // Execute both promises concurrently
      const [
        { data: featuredDeposits, meta: initialPartnerDepositsMeta },
        { data: bestDeposits },
      ] = await Promise.all([featuredDepositsPromise, bestDepositsPromise]);

      // Map deposits data
      const featuredDepositCards: IBankDepositCard[] = featuredDeposits?.map(
        (deposit: any) => MapDepositsFromServer(deposit),
      )!;

      const bestDepositsCards: IBankDepositCard[] = bestDeposits?.map(
        (deposit: any) => MapDepositsFromServer(deposit),
      )!;

      // Fetch translations
      const translations = await serverSideTranslations(locale ?? 'en', [
        'common',
      ]);

      return {
        props: {
          ...translations,
          initialPartnerDeposits: featuredDepositCards,
          initialPartnerDepositsMeta,
          bestDeposits: bestDepositsCards.slice(0, 2),
          preparedTerms,
          country,
        },
      };
    } catch (error) {
      console.log(error);
      return {
        notFound: true,
      };
    }
  })
  .build();

export const MultibankingPlatform: NextPage<any> = ({
  initialPartnerDeposits,
  initialPartnerDepositsMeta,
  bestDeposits,
}) => {
  const { t } = useTranslation();

  return (
    <Main
      meta={
        <Meta
          title={t('page.platform.title')}
          description={t('seo.fixed-deposits.main.description')}
        />
      }
      mobileTitle={t('mobile.best_deposits_for_you')}
      isHeaderMinified
    >
      <MultibankingPlatformLayout
        bestDeposits={bestDeposits}
        initialPartnerDeposits={initialPartnerDeposits}
        initialPartnerDepositsMeta={initialPartnerDepositsMeta}
      />
    </Main>
  );
};

export default MultibankingPlatform;
